import { accessToken } from "views_shared/app/javascript/scripts/authentication";
import { refreshAccessToken } from "views_shared/app/javascript/api/withTokenRefresh";
import qlog from "views_shared/app/javascript/scripts/qlog";

// Non-SUBSCRIBED statuses worth reporting — tier-limit hits + transient drops.
// See https://supabase.com/docs/guides/realtime/limits
const UNHEALTHY_STATUSES = new Set(["CHANNEL_ERROR", "TIMED_OUT", "CLOSED"]);

// One realtime channel per viewer, shared by every subscriber. It opens on the first subscriber and
// closes after the last one leaves. Listeners receive { type: "receipt", receipt } when one of the
// viewer's receipts changes, and { type: "resync" } after a reconnect or tab-resume — a cue to
// refetch, since realtime doesn't replay what it missed while disconnected. The channel lifecycle
// (auth re-arm, rejoin, backfill) is owned here, so consumers are just listeners.

let shared = null; // { profileId, listeners: Set<fn>, close: fn }

function openChannel(profileId, emit) {
  const { supabaseClient } = globalThis.Qwoted;
  const channelName = `receipts-${profileId}`;
  let closing = false;
  let dropped = false;
  let resumedAt = 0;

  const build = () => {
    const ch = supabaseClient.channel(channelName);
    ch.on(
      "postgres_changes",
      {
        event: "*",
        schema: globalThis.Qwoted.POSTGRES_SCHEMA,
        table: "interact_receipts",
        filter: `receiver_profile_id=eq.${profileId}`,
      },
      (payload) => {
        // Broad admin RLS can surface other users' receipts; ignore those.
        if (payload.new?.receiver_profile_id !== profileId) return;
        // eventType ("INSERT" = new message vs "UPDATE" = read/archive) lets consumers decide
        // whether to wait out the mat-refresh lag — only new messages advance last_sent_at.
        emit({ type: "receipt", eventType: payload.eventType, receipt: payload.new });
      },
    );
    ch.subscribe((status, err) => {
      if (status === "SUBSCRIBED") {
        if (dropped) {
          dropped = false;
          emit({ type: "resync" });
        }
        return;
      }
      if (UNHEALTHY_STATUSES.has(status)) {
        dropped = true;
        const message = `Realtime channel ${channelName} entered ${status}`;
        qlog.warn(message, err);
        // Don't report drops we can't act on: teardown, or while backgrounded/just-refocused.
        const benign = closing || document.visibilityState === "hidden" || Date.now() - resumedAt < 10_000;
        if (!benign) window.Rollbar?.error(message, err);
      }
    });
    return ch;
  };

  let channel = build();

  const onVisible = async () => {
    if (document.visibilityState !== "visible") return;
    resumedAt = Date.now();
    // Realtime caches the page-load JWT and can't rejoin once it expires; hand it a fresh token.
    if (!accessToken()) await refreshAccessToken();
    const token = accessToken();
    if (token) supabaseClient.realtime?.setAuth(token);
    // setAuth won't revive a channel that died while hidden — reopen it if it's no longer joined.
    if (channel && channel.state !== "joined") {
      supabaseClient.removeChannel(channel);
      channel = build();
    }
    emit({ type: "resync" });
  };
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    closing = true;
    document.removeEventListener("visibilitychange", onVisible);
    channel.unsubscribe();
    supabaseClient.removeChannel(channel);
  };
}

// Register a listener on the viewer's shared receipts channel; returns an unsubscribe fn. No-ops
// (with a no-op unsubscribe) when there's no signed-in profile or no realtime client.
export default function subscribeToViewerReceipts(listener) {
  const profileId = globalThis.Qwoted?.CURRENT_PROFILE_ID;
  if (!profileId || !globalThis.Qwoted?.supabaseClient) return () => {};

  if (!shared || shared.profileId !== profileId) {
    if (shared) shared.close();
    const listeners = new Set();
    const close = openChannel(profileId, (event) => listeners.forEach((fn) => fn(event)));
    shared = { profileId, listeners, close };
  }

  const { listeners } = shared;
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    // Close the channel once nobody is listening — but only if this is still the live set, so a
    // stale unsubscribe can't tear down a channel rebuilt for a different profile.
    if (listeners.size === 0 && shared?.listeners === listeners) {
      shared.close();
      shared = null;
    }
  };
}
